import { AdvancedSearchSelectInput } from '@framework/form/inputs/advanced-search/AdvancedSearchSelectInput.tsx';
import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { Flex, Space } from 'antd';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPlus } from 'react-icons/tb';
import styled from 'styled-components';
import { IconWrapper } from '../../../icon/IconWrapper.tsx';
import { type SearchableField } from '../../ISearchableField.tsx';
import { getSearchableFieldTypeLabel } from '../utils/getSearchableFieldTypeLabel.ts';
import { FilterBarAddPopoverEditStep } from './FilterBarAddPopoverEditStep.tsx';

const AddPopoverContainer = styled.div`
  min-width: 170px;
  max-width: 320px;

  /* Compensa il padding interno di Ant Design Popover */
  margin: -12px;
`;

const AddPopoverFieldTypeBadge = styled.span`
  color: ${props => props.theme.colorTextTertiary};
  font-size: 11px;
`;

export interface IFilterBarAddPopoverContentProps {
  fields: SearchableField[];
  query: Record<string, any>;
  schema: YupAnyObjectSchema;
  excludeNames: string[];
  onAdded: (name: string) => void;
  onClose: () => void;
}

/**
 * Estratto come componente a sé per sfruttare il `destroyTooltipOnHide` del
 * `Popover` esterno: tenendo qui dentro `step`, `selectedName` e il name
 * "fantasma" del SelectInput, si resettano automaticamente a ogni apertura
 * — anche su Esc / click esterno (che non passano per `onApplied`/`onCancel`/`onBack`).
 */
export function FilterBarAddPopoverContent({
  fields,
  query,
  schema,
  excludeNames,
  onAdded,
  onClose,
}: Readonly<IFilterBarAddPopoverContentProps>) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'pick' | 'edit'>('pick');
  const [selectedName, setSelectedName] = useState<string | null>(null);

  // Name "fantasma" per il SelectInput del pick step: deve stare sotto al
  // FormHandler globale (richiesto da useInputController) ma non finire mai
  // nella query — viene strippato da `.noUnknown()` dello schema yup.
  const searchInputName = `__filterBarSearch__${useId()}`;

  // Esc handler globale: la dropdown del Select è renderizzata in un portal
  // nel <body> (fuori dal FocusTrap del Popover esterno). L'Esc premuto
  // dentro alla dropdown non arriva al FocusTrap → `escapeDeactivates` non
  // scatta. Catturiamo Esc in capture phase per chiudere il popover.
  // Il listener vive solo finché il content è mounted (= popover aperto).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [onClose]);

  // Mappa name → field per recuperare il tipo nel renderOption (SelectOption
  // standard non porta il tipo del field con sé).
  const fieldByName = useMemo(
    () => new Map(fields.map(f => [f.name, f])),
    [fields],
  );

  const availableOptions = useMemo<SelectOption[]>(
    () =>
      fields
        .filter(f => !f.hidden && !excludeNames.includes(f.name))
        .map(f => ({
          value: f.name,
          label: f.label,
          searchable: f.label,
        })),
    [fields, excludeNames],
  );

  const selectedField = useMemo(
    () => fields.find(f => f.name === selectedName) ?? null,
    [fields, selectedName],
  );

  if (step === 'edit' && selectedField) {
    return (
      <FilterBarAddPopoverEditStep
        field={selectedField}
        fields={fields}
        query={query}
        schema={schema}
        onAdded={onAdded}
        onClose={onClose}
        onBack={() => {
          setStep('pick');
          setSelectedName(null);
        }}
      />
    );
  }

  return (
    <AddPopoverContainer>
      <AdvancedSearchSelectInput
        name={searchInputName}
        searchable
        open
        autoFocus
        size="middle"
        placeholder={
          <Space>
            <IconWrapper icon={<TbPlus />} />
            {t('filter-bar.search-field-placeholder', 'Cerca campo…')}
          </Space>
        }
        notFoundContent={t('filter-bar.no-field-found', 'Nessun campo trovato')}
        options={availableOptions}
        renderOption={option => {
          const field = fieldByName.get(option.value as string);
          return (
            <Flex justify="space-between" align="center" gap={8}>
              <span>{option.label}</span>
              {field && (
                <AddPopoverFieldTypeBadge>
                  {getSearchableFieldTypeLabel(field.type, t)}
                </AddPopoverFieldTypeBadge>
              )}
            </Flex>
          );
        }}
        onChange={(value, _option, onChange) => {
          if (value) {
            setSelectedName(value as string);
            setStep('edit');
          }
          // Reset del valore "fantasma" — non strettamente necessario perché
          // il content viene smontato al close, ma evita stati transitori
          // durante il passaggio pick → edit.
          onChange(null);
        }}
        skipResetOnMissingValue
      />
    </AddPopoverContainer>
  );
}
